/**
 * WebSocket context for global connection state management.
 *
 * Provides a single WebSocket connection that can be shared across
 * all components, with automatic reconnection and status tracking.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ConnectionStatus,
  ReconnectingWebSocket,
  getWebSocketUrl,
  onGlobalOrchestrationEvent,
} from '@/services/websocket';
import type { WsEventMessage } from '@/types/websocket';

interface WebSocketContextValue {
  /** Current connection status */
  status: ConnectionStatus;
  /** Manually trigger reconnection */
  reconnect: () => void;
  /** Check if connected */
  isConnected: boolean;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

// Report list queries refreshed on home.live.changed.
const LIST_QUERY_KEYS = [
  'reports',
  'reports-grouped',
  'reports-individual',
  'reports-repositories',
  'reports-branch-filters',
] as const;

// Server-side /reports/grouped cache TTL.
const SERVER_CACHE_TTL_MS = 5000;

interface WebSocketProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages the WebSocket connection.
 * Should be placed near the top of the component tree.
 */
export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const wsRef = useRef<ReconnectingWebSocket | null>(null);
  const queryClient = useQueryClient();
  const lastListInvalidateRef = useRef(0);
  const trailingListTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const invalidateLists = useCallback(() => {
    for (const key of LIST_QUERY_KEYS) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  }, [queryClient]);

  // Drop any pending trailing refetch on unmount.
  useEffect(
    () => () => {
      if (trailingListTimerRef.current !== null) clearTimeout(trailingListTimerRef.current);
    },
    [],
  );

  // Handle incoming WebSocket messages
  const handleMessage = useCallback(
    (event: WsEventMessage) => {
      console.log('[WS] Received event:', event.type);

      switch (event.type) {
        case 'report_created':
          // Invalidate report lists (flat + grouped landing page)
          queryClient.invalidateQueries({ queryKey: ['reports'] });
          queryClient.invalidateQueries({ queryKey: ['reports-grouped'] });
          break;

        case 'report_updated':
          // Invalidate lists, grouped view, consolidated view, and specific report
          queryClient.invalidateQueries({ queryKey: ['reports'] });
          queryClient.invalidateQueries({ queryKey: ['reports-grouped'] });
          queryClient.invalidateQueries({ queryKey: ['reports-consolidated'] });
          queryClient.invalidateQueries({
            queryKey: ['report-detail', event.payload.report_id],
          });
          break;

        case 'report_registered':
        case 'report_entry_updated':
          // Invalidate the specific report group, reports lists, and consolidated view
          queryClient.invalidateQueries({
            queryKey: [
              'report-detail',
              (event.payload as { report_group_id?: string }).report_group_id ??
                event.payload.report_id,
            ],
          });
          queryClient.invalidateQueries({ queryKey: ['reports'] });
          queryClient.invalidateQueries({ queryKey: ['reports-grouped'] });
          queryClient.invalidateQueries({ queryKey: ['reports-consolidated'] });
          break;

        case 'home.live.changed': {
          // Unscoped list signal; orchestration events are identity-scoped.
          // Throttled: at most one immediate refetch per cache window.
          const now = Date.now();
          if (now - lastListInvalidateRef.current >= SERVER_CACHE_TTL_MS) {
            lastListInvalidateRef.current = now;
            invalidateLists();
          }
          // One pending trailing refetch, after the server cache window passes.
          if (trailingListTimerRef.current === null) {
            trailingListTimerRef.current = setTimeout(() => {
              trailingListTimerRef.current = null;
              lastListInvalidateRef.current = Date.now();
              invalidateLists();
            }, SERVER_CACHE_TTL_MS + 1000);
          }
          break;
        }

        case 'suites_available':
          // Invalidate suites for the report
          queryClient.invalidateQueries({
            queryKey: ['report', event.payload.report_id, 'suites'],
          });
          break;
      }
    },
    [queryClient, invalidateLists],
  );

  // Initialize WebSocket connection
  useEffect(() => {
    const ws = new ReconnectingWebSocket(getWebSocketUrl(), {
      onStatusChange: setStatus,
      onMessage: handleMessage,
      onError: (error) => {
        console.error('[WS] Connection error:', error);
      },
    });

    ws.connect();
    wsRef.current = ws;

    return () => {
      ws.disconnect();
      wsRef.current = null;
    };
  }, [handleMessage]);

  // Invalidate lists on orchestration events.
  useEffect(() => {
    return onGlobalOrchestrationEvent((event) => {
      switch (event.type) {
        case 'orchestration.run.started':
        case 'orchestration.unit.leased':
        case 'orchestration.unit.completed':
        case 'orchestration.lease.expired':
        case 'orchestration.run.completed':
        case 'orchestration.run.timed_out':
          queryClient.invalidateQueries({ queryKey: ['reports-grouped'] });
          queryClient.invalidateQueries({ queryKey: ['reports'] });
          break;
      }
    });
  }, [queryClient]);

  // Reconnect function
  const reconnect = useCallback(() => {
    wsRef.current?.reconnect();
  }, []);

  const value: WebSocketContextValue = {
    status,
    reconnect,
    isConnected: status === 'connected',
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

/**
 * Hook to access the WebSocket context.
 * Must be used within a WebSocketProvider.
 */
export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
}

/**
 * Hook to get just the connection status.
 * Convenience wrapper around useWebSocketContext.
 */
export function useWebSocketStatus() {
  const { status, reconnect, isConnected } = useWebSocketContext();
  return { status, reconnect, isConnected };
}
